import { useWindowSize } from 'hooks'
import type React from 'react'
import styled, { css } from 'styled-components'
import { GLOBAL_COLORS } from 'theme'

const ChartWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  ${({ theme }) => theme.mediaWidth.Mobile`
    justify-content: center;
  `};
`

const SVG = styled.svg`
  display: flex;
  justify-content: flex-end;
`

const Rect = styled.rect<{ isActive: boolean; hoverColor: string }>`
  cursor: pointer;
  :hover {
    fill: ${({ hoverColor }) => hoverColor};
  }
  ${({ isActive, hoverColor }) =>
    isActive &&
    css`
      fill: ${hoverColor};
    `}
`

export const BarChart: React.FC<{
  setHighlighted: React.Dispatch<React.SetStateAction<string | undefined>>
  highlighted: string | undefined
  colors: {
    backgroundColor: string
    hoverBackgroundColor: string
  }[]
  chartData: {
    label: string
    data: string[]
  }[]
}> = ({ highlighted, setHighlighted, chartData, colors }) => {
  const { isMobile } = useWindowSize()
  const common = [...chartData.map((el) => el.data.map((string) => Number(string)))].flat()
  const commonMax = Math.max(...common)
  const commonMin = Math.min(...common)

  const isIndex = chartData.length > 1
  const barWidth = isMobile ? 50 : isIndex ? 30 : 50
  const chartHeight = 300
  const halfHeight = chartHeight / 2
  const chartWidth = isMobile ? 280 : 700
  const lineWidth = isMobile ? chartWidth : chartWidth - 30
  const leftPadding = isMobile ? 100 : isIndex ? 10 : 27
  const formattedData = chartData.map(({ data }) => data)
  const data = isIndex
    ? chartData[0].data.flatMap((number, index) => [
        {
          value: number,
          color: colors[0].backgroundColor,
          hoverColor: colors[0].hoverBackgroundColor,
          label: chartData[0].label,
        },
        {
          value: formattedData[1][index],
          color: colors[1].backgroundColor,
          hoverColor: colors[1].hoverBackgroundColor,
          label: chartData[1].label,
        },
      ])
    : chartData[0].data.flatMap((number) => [
        {
          value: +number * 20,
          color: colors[0].backgroundColor,
          hoverColor: colors[0].hoverBackgroundColor,
          label: chartData[0].label,
        },
      ])

  return (
    <ChartWrapper>
      <SVG width={chartWidth} height={chartHeight}>
        <rect x="0" y={halfHeight} height="1" fill={GLOBAL_COLORS.separatorBg} width={lineWidth} />
        <g>
          {data.map((el, index) => {
            const number =
              (Number(el.value) / (commonMax - commonMin)) * (isMobile ? halfHeight / 35 : 100)
            const xValue = leftPadding + index * barWidth
            const xOffset = isIndex ? 43 : 95
            return (
              <Rect
                key={`${index}-${el.value}`}
                fill={el.color}
                hoverColor={el.hoverColor}
                className="bar"
                x={
                  isIndex
                    ? xValue + xOffset * index - (index % 2 !== 0 ? xOffset : 0)
                    : xValue + xOffset * index
                }
                width={barWidth}
                y={number >= 0 ? halfHeight - number : halfHeight + 1}
                height={number >= 0 ? number : number * -1}
                onMouseEnter={() => setHighlighted(el.label)}
                onMouseLeave={() => setHighlighted(undefined)}
                isActive={el.label === highlighted}
              />
            )
          })}
        </g>
      </SVG>
    </ChartWrapper>
  )
}
